import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { Uploadable } from '@app/classes/uploadable';
import { NavigationGuard } from '@app/guards';
import { CanDeactivateComponent } from '@app/interfaces';
import { FileHelperService, NotifyService } from '@app/services';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export type BulkUploadViewStatus = 'invalid' | 'ready' | 'uploading' | 'complete';

/**
 * This abstract class is to be extended when creating a BulkUploadView
 *
 * There are no private methods, so feel free to overwrite any methods.
 *
 * If you have been tasked with building a bulk uploader, you must do 3 things
 * 1. Extend this class and implement the abstract methods.
 * See CompanyDocumentsBulkUploadView component for an example of this.
 * 2. Extend the Uploadable abstract class and implement its methods.
 * See CompanyDocumentUploadable for an example of this.
 * 3. Create a template for the new uploader
 * See CompanyDocumentsBulkUploadView template for an example of this.
 */
@Injectable()
export abstract class BulkUploadView implements OnInit, OnDestroy, CanDeactivateComponent {
    /**
     * The maximum number of files that can be uploaded at once.
     */
    abstract uploadLimit: number;

    /**
     * The type of upload. This is shown in the UI.
     *
     * Ex: "Documents" or "Certificates"
     */
    uploadDescriptor = 'files';

    /**
     * The uploadables to display in the UI
     */
    abstract uploadableCreator: (file: File) => Uploadable;

    /**
     * The uploadables to display in the UI
     */
    abstract uploadables: Uploadable[];

    /**
     * All subscriptions are stored here so they can be unsubscribed from on unmount
     */
    subscriptions: Subscription[] = [];

    /**
     * Required for form control errors
     */
    errorStateMatcher = new ErrorStateMatcher();

    /**
     * The status of the View.
     * The status determines which actions are allowed.
     * Ex: Cannot upload if invalid. Cannot add more files if completed.
     */
    status: BulkUploadViewStatus = 'invalid';

    constructor(
        protected fileHelperService: FileHelperService,
        protected notifyService: NotifyService
    ) {}

    canDeactivate(): boolean {
        if (this.status === 'uploading') {
            return window.confirm(NavigationGuard.warningMessage);
        }

        return true;
    }

    ngOnInit(): void {
        this.onInit();
    }

    ngOnDestroy(): void {
        // Remove all subscriptions on component destroy to prevent memory leaks
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    /**
     * Handles new Files being added
     * Creates a new uploadable from each file and sorts them
     */
    filesAdded(fileList: FileList): void {
        let files = Array.from(fileList);

        if (files.length + this.uploadables.length > this.uploadLimit) {
            const overflowCount = files.length + this.uploadables.length - this.uploadLimit;

            files = files.slice(0, files.length - overflowCount);

            this.notifyService.error({
                key: 'components.bulk-file-uploader.reachedMaximumNumberOfFilesPerUpload',
                params: {
                    uploadLimit: this.uploadLimit,
                },
            });
        }

        const newUploadables = files.map((file) => this.uploadableCreator(file));

        this.subscribeToUploadables(newUploadables);

        // uploadables with invalid files go first
        newUploadables.sort((u: Uploadable) => (u.hasInvalidFile() ? -1 : 1));

        // new uploadables are put above the existing uploadables
        this.uploadables = [...newUploadables, ...this.uploadables];

        // wrapping in setTimeout to stop Angular from mistakenly removing errors
        setTimeout(() => {
            // adding new uploadables might invalidate existing uploadables
            this.validate();
        });

        this.onAdded(newUploadables);
    }

    remove(uploadableToRemove: Uploadable): void {
        this.uploadables = this.uploadables.filter((u) => u !== uploadableToRemove);

        // removing an uploadable might change the validity of other uploadables
        // so we revalidate
        this.validate();
        this.onRemove(uploadableToRemove);
    }

    /**
     * Triggered when the user clicks upload
     *
     * Delegates actual uploading to the onUpload method
     */
    async upload(): Promise<void> {
        await this.validate();

        if (this.status === 'invalid') {
            return;
        }

        this.uploadables.forEach((u) => {
            u.status = 'uploading';
            u.nameFormControl.disable();
        });

        this.updateViewStatus();

        await this.onUpload();

        this.onUploadComplete();
    }

    async reset(): Promise<void> {
        this.uploadables = [];
        this.status = 'invalid';
        this.onReset();
    }

    /**
     * Called on component initialization
     * Can be left empty if no initialization is needed.
     * Useful for loading data.
     */
    protected abstract onInit(): Promise<void>;

    /**
     * Perform additional clean up on view reset
     *
     * note: this method is called from the reset method which handles resetting status and uploadables
     */
    protected abstract onReset(): Promise<void>;

    /**
     * Perform additional actions after files have been added
     *
     * note: this method is called from the fileAdded method which handles adding files to uploadables
     */
    protected abstract onAdded(uploadables: Uploadable[]): void;

    /**
     * Perform additional clean up on file removal
     *
     * note: this method is called from the removed method which handles removing a file from uploadables
     */
    protected abstract onRemove(uploadable: Uploadable): void;

    /**
     * Defines the logic for uploading.
     *
     * Is responsible for updating the status on each uploadable as it completes (or fails)
     *
     * This method will not be called unless all uploadables are valid (ready to be uploaded).
     *
     */
    protected abstract onUpload(): Promise<void>;

    /**
     * Perform any validation changes needed on the collection of uploadables.
     */
    protected abstract onValidate(): Promise<void>;

    /**
     * Sets the validation state for individual uploadables and the group as a whole
     */
    protected async validate(): Promise<void> {
        await this.onValidate();
        this.uploadables.forEach((u) => u.updateErrors());
        this.updateViewStatus();
    }

    protected onUploadComplete(): void {
        this.updateViewStatus();
        this.showCompletionToast();
    }

    /**
     * Should the view accept more files.
     *
     * Used to hide the file input.
     */
    allowMoreFiles(): boolean {
        if (this.uploadables.length === this.uploadLimit) {
            return false;
        }

        const hasStartedUpload =
            this.uploadables.length &&
            this.uploadables.every((u) => u.status === 'uploading' || u.status === 'failed' || u.status === 'complete');

        if (hasStartedUpload) {
            return false;
        }

        return true;
    }

    /**
     * Determines if the group of uploadables, as a whole, is ready invalid, ready, or complete
     */
    protected updateViewStatus(): void {
        if (!this.uploadables.length) {
            this.status = 'invalid';

            return;
        }

        const uploading = this.uploadables.some((u) => u.status === 'uploading');

        if (uploading) {
            this.status = 'uploading';

            return;
        }

        const complete =
            this.uploadables.length && this.uploadables.every((u) => u.status === 'failed' || u.status === 'complete');

        if (complete) {
            this.status = 'complete';

            return;
        }

        const hasInvalidUploadable = this.uploadables.some((u) => !u.isReadyToUpload());

        if (hasInvalidUploadable) {
            this.status = 'invalid';

            return;
        }

        this.status = 'ready';
    }

    protected subscribeToUploadables(newUploadables: Uploadable[]): void {
        const newSubscriptions = newUploadables.map((uploadable) =>
            uploadable.nameFormControl.valueChanges
                .pipe(debounceTime(250), distinctUntilChanged())
                .subscribe(() => this.validate())
        );

        this.subscriptions = [...this.subscriptions, ...newSubscriptions];
    }

    /**
     * Shows a toast after uploading the uploadables
     *
     * Status for the uploadables must be set in onUpload after they complete or fail
     */
    protected showCompletionToast(): void {
        const countSuccessfulUploads = this.uploadables.filter((u) => u.status === 'complete').length;
        const countTotalUploads = this.uploadables.length;
        if (countSuccessfulUploads === 0) {
            this.notifyService.error({
                key: 'components.bulk-file-uploader.failedToUpload',
                params: {
                    uploadDescriptor: this.uploadDescriptor,
                },
            });

            return;
        }

        if (countSuccessfulUploads === countTotalUploads) {
            this.notifyService.success({
                key: 'components.bulk-file-uploader.allDocumentsUploaded',
                params: {
                    uploadDescriptor: this.uploadDescriptor,
                },
            });

            return;
        }

        this.notifyService.success({
            key: 'components.bulk-file-uploader.countOfSuccessfulUploads',
            params: {
                countSuccessfulUploads: countSuccessfulUploads,
                uploadableLength: this.uploadables.length,
                uploadDescriptor: this.uploadDescriptor,
            },
        });
    }
}
